// components
import Button, { ButtonProps } from './components/Button';
import CentredSpinner, { CentredSpinnerProps } from './components/CentredSpinner';
import Calendar, { CalendarDot, CalendarProps, CalendarDotItemProps, CalendarDotProps } from './components/Calendar';
import CalendarInput, { CalendarInputProps } from './components/CalendarInput';
import Checkbox, { CheckboxProps } from './components/Checkbox';
import Dialog, { DialogProps } from './components/Dialog';
import ErrorMessage, { ErrorMessageProps } from './components/ErrorMessage';
import Radio, { RadioProps } from './components/Radio';
import Select, { SelectProps } from './components/Select';
import Icon, { icons, IconName, IconProps } from './components/Icon';
import { IconButton, IconNavLink, IconButtonProps, IconNavLinkProps } from './components/IconButton';
import {
  AvailableShiftCard,
  AvailableShiftCardProps,
  CanceledShiftCard,
  CanceledShiftCardProps,
  CardLabel,
  CardLabelProps,
  CardRow,
  CardRowProps,
  CardValue,
  CardValueProps,
  DetailShiftCard,
  DetailShiftCardProps,
  UpcomingShiftCard,
  UpcomingShiftCardProps
} from './components/ShiftCard';
import Grid, { GridProps } from './components/Grid';
import GridChild, { GridChildProps } from './components/GridChild';
import TabLink, { TabLinkProps } from './components/TabLink';

// helpers
import { formatAmount } from './helpers/currency';
import { formatDate, formatTime } from './helpers/date';
import { retrieveErrorMessages } from './helpers/errorHandling';
import { displayShortDuration } from './helpers/duration';
import {
  getCancelationReasonLabel,
  getCostCategoryLabel,
  getShiftTypeLabel,
  getUserStatusLabel
} from './helpers/labels';
import { sortAscending, sortAvailableShiftsByDate, sortShiftsByDate } from './helpers/sort';

// hooks
import useClickOutside from './hooks/useClickOutside';
import useDateIntervalFromMonth from './hooks/useDateIntervalFromMonth';
import useKeydown from './hooks/useKeydown';

// theme
import { SchedulingTheme, SchedulingThemeType, ButtonVariants, computeButtonColors } from './themes';

// design tokens
import BorderWidthTokens from './tokens/borderWidths';
import ColorTokens from './tokens/colors';
import FontFamilyTokens from './tokens/fontFamilies';
import FontSizeTokens from './tokens/fontSizes';
import FontWeightTokens from './tokens/fontWeights';
import LetterSpacingTokens from './tokens/letterSpacings';
import LineHeightTokens from './tokens/lineHeights';
import MediaQueryTokens from './tokens/mediaQueries';
import OpacityTokens from './tokens/opacities';
import RadiiTokens from './tokens/radii';
import ShadowTokens from './tokens/shadows';
import SpacingTokens from './tokens/spacing';
import ZIndiceTokens from './tokens/zIndices';

const Tokens = {
  BorderWidthTokens,
  ColorTokens,
  FontFamilyTokens,
  FontSizeTokens,
  FontWeightTokens,
  LetterSpacingTokens,
  LineHeightTokens,
  MediaQueryTokens,
  OpacityTokens,
  RadiiTokens,
  ShadowTokens,
  SpacingTokens,
  ZIndiceTokens
};

export type {
  // components
  AvailableShiftCardProps,
  ButtonProps,
  CalendarProps,
  CalendarDotProps,
  CalendarDotItemProps,
  CalendarInputProps,
  CanceledShiftCardProps,
  CardLabelProps,
  CardRowProps,
  CardValueProps,
  CentredSpinnerProps,
  CheckboxProps,
  DetailShiftCardProps,
  DialogProps,
  ErrorMessageProps,
  GridProps,
  GridChildProps,
  IconName,
  IconProps,
  IconButtonProps,
  IconNavLinkProps,
  RadioProps,
  SelectProps,
  TabLinkProps,
  UpcomingShiftCardProps,
  // themes
  ButtonVariants,
  SchedulingThemeType
};

export {
  // components
  AvailableShiftCard,
  Button,
  Calendar,
  CalendarDot,
  CalendarInput,
  CardLabel,
  CanceledShiftCard,
  CardRow,
  CardValue,
  CentredSpinner,
  Checkbox,
  DetailShiftCard,
  Dialog,
  ErrorMessage,
  Grid,
  GridChild,
  icons,
  Icon,
  IconButton,
  IconNavLink,
  Radio,
  Select,
  TabLink,
  UpcomingShiftCard,
  // helpers
  displayShortDuration,
  formatAmount,
  formatDate,
  formatTime,
  getCancelationReasonLabel,
  getCostCategoryLabel,
  getShiftTypeLabel,
  getUserStatusLabel,
  retrieveErrorMessages,
  sortAscending,
  sortAvailableShiftsByDate,
  sortShiftsByDate,
  // hooks
  useClickOutside,
  useDateIntervalFromMonth,
  useKeydown,
  // themes
  computeButtonColors,
  SchedulingTheme,
  // design tokens
  Tokens
};
